import { useTranslation } from 'next-i18next'
import { useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFractionImproperToMixed from './useFractionMixedToImproper'

function FractionMixedToImproper({
  difficultyLevel,
  answerState,
  actionButtonsFC,
  autofocus = true,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const { question, formValid, inputs, error, onInputChange, handleCheckAnswer, goToNextQuestion } =
    useFractionImproperToMixed({
      difficultyLevel,
      onAnswerStateChange,
      onNextQuestionClick,
    })

  // `answer` (the improper-fraction solution) stays out.
  useEmitInstance(
    onInstanceChange,
    question
      ? `${question.quotient} \\frac{${question.numerator}}{${question.denominator}} = \\frac{\\square}{\\square}`
      : null,
  )

  const handleGoToNextQuestion = (): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (formValid) {
      hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="flex items-center">
          <span className="text-2xl p-1">{question?.quotient}</span>
          <div className="flex flex-col items-center mr-1">
            <span className="text-2xl">{question?.numerator}</span>
            <Divider />
            <span className="text-2xl">{question?.denominator}</span>
          </div>
          <Equals />
          <div className="flex flex-col items-center ml-1 space-y-2">
            <Input
              aria-label={t('ariaLabelButtons.insertNumerator')}
              autoFocus={autofocus}
              isError={error?.numerator}
              key={`${inputKey}-first`}
              maxDigits={3}
              onChange={(value) => onInputChange('numerator', value)}
              onEnter={onEnterPress}
              ref={inputRef}
              value={`${inputs?.numerator || ''}`}
            />
            <Divider />
            <Input
              aria-label={t('ariaLabelButtons.insertDenominator')}
              isError={error?.denominator}
              key={`${inputKey}-second`}
              maxDigits={3}
              onChange={(value) => onInputChange('denominator', value)}
              onEnter={onEnterPress}
              value={`${inputs?.denominator || ''}`}
            />
          </div>
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default FractionMixedToImproper
